/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.21.1
 * @since 1.0.0
 */
import { createElement, createRef, MouseEvent, PureComponent } from 'react';

import type { TOverflow } from '@/main/types/globalTypes';

import ArrowTooltip from '../Tooltip/Tooltip';
import type { IArrowTooltipProps } from '../Tooltip/Tooltip';
import { EllipsisTextStyled } from './styled/EllipsisTextStyled';

interface IEllipsisTextProps extends Pick<IArrowTooltipProps, 'title' | 'titleAlign'>{
    lines?: number;
    onTooltipClick?: (e: MouseEvent) => void;
    showTooltipOnOverflow: boolean;
    tooltipMsg?: string;
    wrapper?: string;
    wrapperProps?: any;
    overflow?: TOverflow;
    maxHeight?: string;
}

interface IEllipsisTextState {
    isOverflow: boolean;
}

class EllipseText extends PureComponent<IEllipsisTextProps, IEllipsisTextState> {
    static defaultProps = {
        showTooltipOnOverflow: true,
    };
    // ref to ellipse container
    ellipseRef = createRef<HTMLDivElement>();
    state = {
        isOverflow: false,
    };

    /**
     * @description Tooltip should be active if flag `showTooltipOnOverflow` true
     * and Text is overflow or Tooltip have additional message to show (then it always show)
     * @return {boolean}
     */
    isTooltipShow = (): boolean =>
        this.props.showTooltipOnOverflow && (this.state.isOverflow || !!this.props.tooltipMsg);

    /**
     * @description Check text on overflow
     * @return {boolean}
     */
    isComponentOverflow = (): boolean =>
        this.ellipseRef.current !== null &&
        (this.ellipseRef.current.offsetWidth < this.ellipseRef.current.scrollWidth
            || this.ellipseRef.current.offsetHeight < this.ellipseRef.current.scrollHeight);

    componentDidMount(): void {
        this.setState({ isOverflow: this.isComponentOverflow() });
    }

    mouseEnterHandler = (): void => {
        if (!this.state.isOverflow && this.isComponentOverflow()) {
            this.setState({ isOverflow: true });
        } else if (this.state.isOverflow && !this.isComponentOverflow()) {
            this.setState({ isOverflow: false });
        }
    };

    render() {
        return (
            <ArrowTooltip
                disableHoverListener={!this.isTooltipShow()}
                maxHeight={this.props.maxHeight}
                message={this.props.tooltipMsg}
                overflow={this.props.overflow}
                placement="top"
                title={this.props.title || ''}
                titleAlign={this.props.titleAlign}
                onClick={this.props.onTooltipClick}
                onMouseEnter={this.mouseEnterHandler}
            >
                {
                    this.props.wrapper ?
                        createElement(this.props.wrapper, {
                            ref: this.ellipseRef, ...this.props.wrapperProps,
                        }, this.props.children)
                        :
                        <EllipsisTextStyled className="arrow-tooltip__text" lines={this.props.lines} ref={this.ellipseRef}>
                            {this.props.children}
                        </EllipsisTextStyled>
                }
            </ArrowTooltip>
        );
    }
}

export default EllipseText;
